import { getChatCompletion, getChatCompletionStream } from '../../api/chat';
import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { FaPaperPlane, FaRobot, FaCog, FaPlus, FaTimes, FaEdit } from 'react-icons/fa';
import { useBaseComponentFullscreen } from './BaseComponent';

const CHAT_SIZES = {
  small: { height: '220px' },
  medium: { height: '330px' },
  large: { height: '440px' }
};

export default function ChatComponent({ component, onUpdate, refreshSidebar }) {
  const isFullscreen = useBaseComponentFullscreen();
  const data = component.data || { 
    system_message: 'You are a helpful assistant. Answer questions concisely and helpfully.',
    chat_size: 'medium',
    tabs: [{ name: "Chat 1", history: [] }],
    active_tab: 0
  };
  
  const activeChat = data.tabs[data.active_tab] || data.tabs[0];
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [tempSystemMessage, setTempSystemMessage] = useState(data.system_message);
  const [tempChatSize, setTempChatSize] = useState(data.chat_size);
  const [editingTabIndex, setEditingTabIndex] = useState(null);
  const [editTabName, setEditTabName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  const currentSize = CHAT_SIZES[data.chat_size];

  // Initialize with system message if empty
  useEffect(() => {
    if (activeChat.history.length === 0) {
      const updatedTabs = [...data.tabs];
      updatedTabs[data.active_tab].history = [{
        role: 'system',
        content: data.system_message
      }];
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
    }
  }, []);

  // Scroll to bottom of messages
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [activeChat.history]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = {
      role: 'user',
      content: input
    };
    
    const newHistory = [...activeChat.history, userMessage];
    const updatedTabs = [...data.tabs];
    updatedTabs[data.active_tab].history = newHistory;
    
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: updatedTabs
      }
    });
    
    setInput('');
    setIsLoading(true);
    
    try {
      const response = await getChatCompletion(
        newHistory, 
        localStorage.getItem('access_token')
      );
      
      updatedTabs[data.active_tab].history = [...newHistory, {
        role: 'assistant',
        content: response.content
      }];
      
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }
    } catch (error) {
      updatedTabs[data.active_tab].history = [...newHistory, {
        role: 'assistant',
        content: `Error: ${error.message}`
      }];
      
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  const saveSettings = () => {
    // Update system message in all tabs' first message
    const updatedTabs = data.tabs.map(tab => ({
      ...tab,
      history: tab.history.map((msg, i) => 
        i === 0 ? { ...msg, content: tempSystemMessage } : msg
      )
    }));
    
    onUpdate({
      ...component,
      data: {
        ...data,
        system_message: tempSystemMessage,
        chat_size: tempChatSize,
        tabs: updatedTabs
      }
    });
    setShowSettings(false);
  };

  const addTab = () => {
    const newTabs = [
      ...data.tabs,
      {
        name: `Chat ${data.tabs.length + 1}`,
        system_message: 'You are a helpful assistant. Answer questions concisely and helpfully.',
        history: [{
          role: 'system',
          content: 'You are a helpful assistant. Answer questions concisely and helpfully.'
        }]
      }
    ];
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: newTabs.length - 1
      }
    });
  };

  const deleteTab = (index) => {
    const newTabs = data.tabs.filter((_, i) => i !== index);
    onUpdate({
      ...component,
      data: {
        ...data,
        tabs: newTabs,
        active_tab: Math.min(data.active_tab, newTabs.length - 1)
      }
    });
    setDeleteConfirm(null);
  };

  const startEditingTab = (index) => {
    setEditingTabIndex(index);
    setEditTabName(data.tabs[index].name);
  };

  const saveTabName = () => {
    if (editTabName.trim()) {
      const updatedTabs = [...data.tabs];
      updatedTabs[editingTabIndex].name = editTabName.trim();
      onUpdate({
        ...component,
        data: {
          ...data,
          tabs: updatedTabs
        }
      });
      setEditingTabIndex(null);
    }
  };

  return (
    <div className={`flex flex-col ${isFullscreen ? 'h-full' : ''}`.trim()}>
      {/* Tab bar */}
      <div className="tab-bar flex flex-wrap items-center gap-1 mb-2">
        {data.tabs.map((tab, index) => (
          <div
            key={index}
            className={`flex items-center gap-1 px-2 py-1 rounded-md cursor-pointer transition-colors text-sm ${
              index === data.active_tab 
                ? 'bg-[var(--primary)] text-white'
                : 'bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]'
            }`}
            onClick={() => onUpdate({
              ...component,
              data: {
                ...data,
                active_tab: index
              }
            })}
          >
            {editingTabIndex === index ? (
              <input
                type="text"
                className="px-1 bg-transparent rounded focus:outline-none w-20"
                value={editTabName}
                onChange={(e) => setEditTabName(e.target.value)}
                onBlur={saveTabName}
                onKeyPress={(e) => e.key === 'Enter' && saveTabName()}
                autoFocus
              />
            ) : (
              <>
                <span className={`truncate ${isFullscreen ? 'max-w-[200px]' : 'max-w-[120px]'}`} title={tab.name}>{tab.name}</span>
                <button 
                  className="p-1 hover:opacity-80"
                  onClick={(e) => {
                    e.stopPropagation();
                    startEditingTab(index);
                  }}
                >
                  <FaEdit size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
                </button>
              </>
            )}
            
            {data.tabs.length > 1 && (
              <button
                className="p-1 hover:opacity-80"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteConfirm(index);
                }}
              >
                <FaTimes size={10} className={index === data.active_tab ? 'text-white' : 'text-[var(--text-secondary)]'} />
              </button>
            )}
          </div>
        ))}
        
        <button
          className="p-1 rounded-md bg-[var(--bg-hover)] hover:bg-[var(--primary)] hover:text-white transition-colors text-sm"
          onClick={addTab}
          title="Add new chat"
        >
          <FaPlus size={12} />
        </button>
      </div>

      {/* Chat messages */}
      <div 
        ref={chatContainerRef}
        className={
          isFullscreen
            ? 'flex-1 min-h-0 overflow-y-auto p-4 space-y-4 bg-[var(--bg-primary)] rounded-t-lg'
            : 'flex-1 overflow-y-auto p-4 space-y-4 bg-[var(--bg-primary)] rounded-t-lg'
        }
        style={
          isFullscreen
            ? { }
            : { height: currentSize.height, maxHeight: currentSize.height, minHeight: currentSize.height }
        }
      >
        {activeChat.history
          .filter(m => m.role !== 'system')
          .map((message, index) => (
            <div 
              key={index} 
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div 
                className={`max-w-[80%] rounded-lg p-3 text-sm ${
                  message.role === 'user' 
                    ? 'bg-[var(--primary)] text-white' 
                    : 'bg-[var(--bg-hover)] text-[var(--text-primary)]'
                }`}
              >
                <ReactMarkdown>
                  {message.content}
                </ReactMarkdown>
              </div>
            </div>
          ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-[var(--bg-hover)] text-[var(--text-primary)] rounded-lg p-3 text-sm">
              <FaRobot className="animate-pulse" />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      
      {/* Input area */}
      <div className="p-2 bg-[var(--bg-primary)] rounded-b-lg border-t border-[var(--bg-hover)]">
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setTempSystemMessage(data.system_message);
              setTempChatSize(data.chat_size);
              setShowSettings(true);
            }}
            className="p-2 text-[var(--text-secondary)] hover:text-[var(--primary)]"
            title="Chat settings"
          >
            <FaCog />
          </button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                if (e.shiftKey) {
                  // Shift+Enter: add new line (default behavior)
                  return;
                } else {
                  // Enter alone: send message
                  e.preventDefault();
                  sendMessage();
                }
              }
            }}
            placeholder="Type your message..."
            className="flex-1 p-2 border border-[var(--bg-hover)] bg-[var(--bg-primary)] text-[var(--text-primary)] rounded-l focus:outline-none focus:ring-1 focus:ring-[var(--primary)] w-full resize-none min-h-[40px] max-h-[120px]"
            disabled={isLoading}
            rows={1}
            style={{
              overflowY: input.split('\n').length > 2 ? 'scroll' : 'hidden',
              height: 'auto'
            }}
            onInput={(e) => {
              // Auto-resize textarea
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
            className="p-2 bg-[var(--primary)] text-white rounded-r hover:bg-[var(--primary-dark)] disabled:opacity-50"
          >
            <FaPaperPlane />
          </button>
        </div>
      </div>

      {/* Settings popup */}
      {showSettings && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-4 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto border border-[var(--bg-hover)]">
            <h3 className="text-lg mb-4 text-[var(--text-primary)]">Chat Settings</h3>
            
            <div className="mb-4">
              <label className="block text-sm mb-2 text-[var(--text-secondary)]">System Message</label>
              <textarea
                className="w-full h-32 p-2 bg-[var(--bg-primary)] text-[var(--text-primary)] border border-[var(--bg-hover)] rounded"
                value={tempSystemMessage}
                onChange={(e) => setTempSystemMessage(e.target.value)}
                placeholder="Enter system message..."
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm mb-2 text-[var(--text-secondary)]">Chat Size</label>
              <div className="flex gap-2">
                {Object.keys(CHAT_SIZES).map(size => (
                  <button
                    key={size}
                    className={`px-3 py-1 rounded capitalize ${
                      tempChatSize === size
                        ? 'bg-[var(--primary)] text-white'
                        : 'bg-[var(--bg-hover)] text-[var(--text-primary)]'
                    }`}
                    onClick={() => setTempChatSize(size)}
                  >
                    {size}
                  </button>
                ))}
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button 
                className="px-3 py-1 rounded bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]"
                onClick={() => setShowSettings(false)}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 rounded bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-white"
                onClick={saveSettings}
              >
                Save
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Delete confirmation modal */}
      {deleteConfirm !== null && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-[var(--bg-primary)] p-4 rounded-lg max-w-sm w-full mx-4 max-h-[90vh] overflow-y-auto border border-[var(--bg-hover)]">
            <p className="mb-4 text-[var(--text-primary)]">Are you sure you want to delete "{data.tabs[deleteConfirm]?.name}"?</p>
            <div className="flex justify-end gap-2">
              <button 
                className="px-3 py-1 rounded bg-[var(--bg-hover)] hover:bg-[var(--bg-hover)] text-[var(--text-primary)]"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button 
                className="px-3 py-1 rounded bg-danger hover:bg-danger hover:scale-105 hover:shadow-lg text-white transition-all duration-200"
                onClick={() => deleteTab(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}